/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';

import { environment } from 'src/environments/environment-variables';
import firebase from 'firebase/compat/app';
import { translateToGerman } from 'utils/basic-utils';

import { REPORT_FAILED_TO_OPEN_WINDOW } from '@touchplan/strings';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { IProjectReportsFbdbRequestResults } from './fbdb-requests.interfaces';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { lastValueFrom } from 'rxjs';

const requestPath = 'fbdbRequests';

const stringTranslate = `
(function(){
	var sectionsToChange = [
		".report-controller .left-container",
		".report-controller .center-container",
		".weekly-work-report-table thead",
		".sixweek-header",
		".gantt-tbl thead",
		".ppc-by-week-tbl thead",
		".constraint-log-table thead"
	];

	var replacements = [
		{orig: /^Plans/, replacement: "Pläne"},
		{orig: /^Roles/, replacement: "Gewerke"},
		{orig: /^Locations/, replacement: "Standorte"},
		{orig: /^Start Date/, replacement: "Startdatum"},
		{orig: /^End Date/, replacement: "Enddatum"},
		{orig: "Data as of", replacement: "Daten per"},
		{orig: "Weeks Commencing", replacement: "Woche beginnend"},
		{orig: "Week Commencing", replacement: "Woche beginnend"},
		{orig: "Task Description", replacement: "Aufgabenbeschreibung"},
		{orig: "All Plans", replacement: "Alle Pläne"},
		{orig: "All Roles", replacement: "Alle Gewerke"},
		{orig: "All Locations", replacement: "Alle Standorte"},
		{orig: "Prerequisite Work", replacement: "Notwendige Vorraussctzung"},
		{orig: "Project Roles", replacement: "Projekt Gewerke"},
		{orig: "Prior?", replacement: "vorherige woche?"},
		{orig: "Crew Size", replacement: "Kolonnengröße"},
		{orig: "Next", replacement: "nächste woche"},
		{orig: "Complete", replacement: "erledigt"},
		{orig: "Met Promise", replacement: "Zusage eingehalten"},
		{orig: "Reason for Variance", replacement: "Grund für Abweichung"},
		{orig: "Progress Notes", replacement: "Fortschrittsbemerkung"},
		{orig: "Notes", replacement: "Bemerkungen"},
		{orig: "Constraints", replacement: "Einschränkungen"},
		{orig: "Reason For Variance", replacement: "Grund für Abweichung"},
		{orig: "Six Week Look Ahead", replacement: "6-Wochenvorschau"},
		{orig: "Gantt Project Report", replacement: "Balkenplan"},
		{orig: "Days", replacement: "Tage"},
		{orig: "Planned Start", replacement: "Geplanter Anfang"},
		{orig: "Planned Finish", replacement: "Geplantes Ende"},
		{orig: "Actual Finish", replacement: "Tatsächliches Ende"},
		{orig: "Bold italic indicates a constraint", replacement: "Fett kursiv bedeutet eine Einschränkung"},
		{orig: "PPC By Week", replacement: "Wöchentlicher PEA Wert"},
		{orig: "Promises Met", replacement: "Zusage eingehalten"},
		{orig: "Promises Made", replacement: "gemachte Zusagen"},
		{orig: "Combined", replacement: "verbunden"},
		{orig: "PPC by Variance Reason", replacement: "PEA Nach Abweichungsgrund"},
		{orig: "PPC by Variance", replacement: "PEA Nach Abweichungsgrund"},
		{orig: "PPC Variance By Role", replacement: "Fett kursiv bedeutet eine Einschränkung"},
		{orig: "Constraint Description", replacement: "Beschreibung der Einschränkung"},
		{orig: "Constrained Tasks", replacement: "Einschränkende Aufgaben"},
		{orig: "Date Identified", replacement: "Tag der Feststellung"},
		{orig: "Date Promised", replacement: "Zugesagtes Datum"},
		{orig: "Reponsible Party", replacement: "Verantwortlicher"},
		{orig: "Notes", replacement: "Tag der Feststellung"},
		{orig: "Current Plan", replacement: "Aktueller Plan"},
		{orig: "Original Milestone", replacement: "Original Meilenstein"},
		{orig: "Days current schedule is before or after milestone", replacement: "Aktueller Plan ist vor oder nach Meilenstein"},
		{orig: "Work Days", replacement: "Arbeitstage"},
		{orig: "Required Date", replacement: "Zugesagtes Datum"},
		{orig: "Planned Date", replacement: "Geplantes Datum"},
		{orig: "Responsible Project Member", replacement: "Verantwortlicher"},
		{orig: "Assigned To", replacement: "Verantwortlicher"},
		{orig: "Week", replacement: "Woche"}
	];

	var walkDOM = function (node) {
		if(!node){return;}
		else if(node.hasChildNodes()){
			node.childNodes.forEach(function(childNode){ walkDOM(childNode); });
		}
		else{
			if(node.nodeType === 3){ //text
				var s = node.nodeValue.trim();
				if(!s){return;}

				replacements.forEach(function(obj){
					node.nodeValue = node.nodeValue.replace(obj.orig, obj.replacement);
				});
			}
		}
	};

	function walkDOMList(list){
		list.forEach(function(selector){
			var matches = document.querySelectorAll(selector);
			matches.forEach(function(m){
				walkDOM(m);
			})
		});
	}

	return function(){ walkDOMList(sectionsToChange); }
})();
`;

const legacyButtonStyling = `
<style>
button,
.button {
	display: inline-block;
	padding: 8px 20px;
	font-size: 13px;
	font-weight: 300;
	color: #ffffff;
	text-align: center;
	border-radius: 3px;
	border: 1px solid transparent;
	background-color: #4fbddb;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
	cursor: pointer;
	min-width: 90px;
}

button:hover {
	background-color: #48caed;
	-webkit-box-shadow: 0 1px 2px rgba(64, 64, 64, 0.25);
	-moz-box-shadow: 0 1px 2px rgba(64, 64, 64, 0.25);
	box-shadow: 0 1px 2px rgba(64, 64, 64, 0.25);
}
button:focus,
button:active {
	outline: none;
}

button:active {
	-webkit-box-shadow: inset 0 1px 2px rgba(62, 179, 211, 0.7);
	-moz-box-shadow: inset 0 1px 2px rgba(62, 179, 211, 0.7);
	box-shadow: inset 0 1px 2px rgba(62, 179, 211, 0.7);
}
</style>
`;

@Injectable()
export class ReportBlobber {
	public storage = firebase.storage();
	public goCloud = environment.useCloud;
	private logger: ILoggerService;

	constructor(private http: HttpClient) {
		this.logger = LoggerServiceFactory.create('ReportBlobber');
	}

	public getCloudThing(path: string) {
		const storageRef = this.storage.ref(path);
		this.logger.log('reportRequest - pre-cloud url get');
		return storageRef.getDownloadURL().then((url) => {
			this.logger.log('reportRequest - cloud url get', !!url);
			return this.requestCloudBlob(url);
		});
	}

	public requestCloudBlob(url: string) {
		if (!url) {
			throw new Error('Cloud blob requires a url. Maybe you need to enable cloud storage?');
		}
		this.logger.log('reportRequest - pre-cloud blob request');
		return lastValueFrom(this.http.get(url, { responseType: 'text' }));
	}

	public legacyReportAssembly(resultData: any) {
		if (resultData['report']) {
			return resultData['report'];
		} else if (resultData['report-0']) {
			let i = 0;
			let tableAssembly = '';
			while (resultData['report-' + i]) {
				tableAssembly += resultData['report-' + i];
				i++;
			}
			return tableAssembly;
		} else {
			this.logger.log('no report...');
			throw new Error("report doesn't exist");
		}
	}

	public shouldGermanify(projectId: string): Promise<boolean> {
		const projectPath = fancyFirebaseGetters.getProjectsPath(projectId);
		return fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(projectPath, projectId)
			.child('germanify')
			.once('value')
			.then((request) => !!request.val());
	}

	public requestResult(requestId: string): Promise<firebase.database.DataSnapshot> {
		const fbdbRequestPath = fancyFirebaseGetters.getFbdbRequestsPath(requestId);
		return fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(fbdbRequestPath)
			.once('value')
			.then((request) => {
				if (!request.exists()) {
					throw new Error('this report has been removed');
				}
				return request;
			});
	}

	public removeRequest(requestId: string) {
		const fbdbRequestPath = fancyFirebaseGetters.getFbdbRequestsPath(requestId);
		return fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(fbdbRequestPath).remove();
	}

	//TODO: replace reportType type with an enum (should be ReportType + PpcFormat + other nonsense)
	public getReport(reportBodyBlob: string, reportType: string, projectId: string) {
		return Promise.all([this.getCloudThing(reportBodyBlob), this.shouldGermanify(projectId)]).then(
			([cloudData, germanify]) => {
				return this.rawHtmlModification(cloudData, reportType, germanify);
			}
		);
	}

	private rawHtmlModification(cloudData: string, reportName: string, shouldGermanify: boolean) {
		this.logger.log('reportRequest - cloud blob fully resolved');
		let doctoredData = cloudData;
		if (shouldGermanify) {
			doctoredData = doctoredData.replace(
				'<button onclick="window.print()" ng-hide="reports.loading">Print</button>',
				'<button  onclick="window.translateToGerman()" ng-hide="reports.loading">Übersetzen</button> <button onclick="window.print()" ng-hide="reports.loading">Print</button>'
			);
		}

		doctoredData =
			`<script type="text/javascript"> window.translateToGerman = ${stringTranslate}</script>` + doctoredData;

		//temp
		// doctoredData = doctoredData.replace(`<button ng-click="reports.back()" ng-hide="reports.loading" class="secondary-action">Close</button>`, `<button onclick="reportsBack()" ng-hide="reports.loading" class="secondary-action">Close</button>`)

		const google = `<script type="text/javascript" src="https://www.gstatic.com/charts/loader.js"></script><script>google.charts.load('current', {'packages':['corechart']});</script>`;
		const jquery = `<script src="https://code.jquery.com/jquery-3.3.1.min.js" integrity="sha256-FgpCb/KJQlLNfOu91ta32o/NMZxltwRo8QtmkMRdAu8=" crossorigin="anonymous"></script>`;
		const reportSpecificStyling = reportName === 'projectReportBundle' ? 'projectReportBundle' : reportName;
		// this.logger.log('doctoredData', doctoredData);

		return `<html><head>${legacyButtonStyling}<title>Touchplan</title>${google} ${jquery}</head><body class="report-controller ${reportSpecificStyling}">${doctoredData}</body></html>`;
	}

	public getReportLegacy(requestId: string, projectId: string) {
		return Promise.all([this.requestResult(requestId), this.shouldGermanify(projectId)])
			.then((results) => {
				const germanify = results[1];
				const request = results[0];

				this.logger.log('reportRequest - request and projectData get');

				if (this.goCloud) {
					return this.getCloudThing(request.child('results').child('reportBodyBlob').val()).then(
						(reportData) => {
							this.logger.log('reportRequest - cloud blob resolved');
							return {
								data: reportData,
								germanify,
								reportName: request.child('name').val() as string,
							};
						}
					);
				} else {
					return {
						data: this.legacyReportAssembly(request.child('results').val()),
						germanify,
						reportName: request.child('name').val() as string,
					};
				}
			})
			.then((reportData) => {
				this.logger.log('reportRequest - cloud blob fully resolved');
				let doctoredData = reportData.data;
				if (reportData.germanify) {
					doctoredData = doctoredData.replace(
						'<button onclick="window.print()" ng-hide="reports.loading">Print</button>',
						'<button  onclick="window.translateToGerman()" ng-hide="reports.loading">Übersetzen</button> <button onclick="window.print()" ng-hide="reports.loading">Print</button>'
					);
				}

				doctoredData =
					`<script type="text/javascript"> window.translateToGerman = ${stringTranslate}</script>` +
					doctoredData;

				//temp
				// doctoredData = doctoredData.replace(`<button ng-click="reports.back()" ng-hide="reports.loading" class="secondary-action">Close</button>`, `<button onclick="reportsBack()" ng-hide="reports.loading" class="secondary-action">Close</button>`)

				const google = `<script type="text/javascript" src="https://www.gstatic.com/charts/loader.js"></script><script>google.charts.load('current', {'packages':['corechart']});</script>`;
				const jquery = `<script src="https://code.jquery.com/jquery-3.3.1.min.js" integrity="sha256-FgpCb/KJQlLNfOu91ta32o/NMZxltwRo8QtmkMRdAu8=" crossorigin="anonymous"></script>`;
				const reportSpecificStyling =
					reportData.reportName === 'projectReportBundle' ? 'projectReportBundle' : reportData.reportName;
				// this.logger.log('doctoredData', doctoredData);

				return `<html><head><title>Touchplan</title>${google} ${jquery}</head><body class="report-controller ${reportSpecificStyling}">${doctoredData}</body></html>`;
			});
	}

	public generateAndOpenReport(reportHtml: string): void {
		const newWindow = window.open();
		if (!newWindow) {
			window.Logging.warning(REPORT_FAILED_TO_OPEN_WINDOW);
			return;
		}

		newWindow.document.write(`<div id="report-loader-blocker">Loading. Please wait...</div>`);

		setTimeout(() => {
			newWindow.document.write(reportHtml);

			for (let i = 0; i < document.styleSheets.length; i++) {
				const styleElement = newWindow.document.createElement('style');
				newWindow.document.head.appendChild(styleElement);

				const pageStylesheet: any = document.styleSheets[i];
				const sheet: any = styleElement.sheet;
				try {
					for (let rule = pageStylesheet.cssRules.length; rule >= 0; rule--) {
						if (pageStylesheet.cssRules[rule] && pageStylesheet.cssRules[rule].cssText) {
							sheet.insertRule(pageStylesheet.cssRules[rule].cssText);
						}
					}
				} catch (e) {
					this.logger.error('Error injecting styling into report', e);
				}
			}

			// This tells the browser we stopped writing.
			// See: https://stackoverflow.com/questions/31387380/document-created-with-window-open-never-reaches-complete-readystate
			newWindow.document.close();
		});

		// MOC-3086
		function initReports() {
			if (newWindow.initializeReports) {
				newWindow.initializeReports.forEach((fn: any) => {
					fn();
				});
			}
			newWindow.document.getElementById('report-loader-blocker').outerHTML = '';
		}

		newWindow.onload = initReports;
	}

	public getCompleteReport(requstId: string, projectId: string) {}
}
